import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {WorksheetViewQuery} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import {DefaultStatusDisplayName} from 'processes/ProcessConstants';
import {HasAvatar, NO_AVATAR_URL} from 'ui-library/avatar/Avatar';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

export type Worksheet = ExtractByTypename<WorksheetViewQuery['worksheet'], 'WorksheetNode'>;

export type WorksheetStepGroup = Worksheet['worksheetStepGroups'][number];
export type WorksheetStep = WorksheetStepGroup['worksheetSteps'][number];

export type TransitionGQL = ExtractByTypename<
  ExtractByTypename<
    WorksheetStepGroup['worksheetSteps'][number]['tasks'][number],
    'ProcessTaskNode'
  >['taskSchema'],
  'ProcessTaskSchemaNode'
>['statusLifecycle']['transitions'][number];

export type MethodTaskOrInaccessible = Worksheet['tasks'][number];
export type MethodTask = ExtractByTypename<MethodTaskOrInaccessible, 'ProcessTaskNode'>;
type StepTasksOrInaccessible = WorksheetStepGroup['worksheetSteps'][number]['tasks'];
export type StepTaskOrInaccessibleGQL = StepTasksOrInaccessible[number];
export type StepTaskGQL = ExtractByTypename<StepTaskOrInaccessibleGQL, 'ProcessTaskNode'>;

export type StepTaskOrInaccessible = StepTask | InaccessibleInfoGraphQL;
export type Task = Worksheet['tasks'][number] | StepTaskOrInaccessible;

export interface Transition {
  __typename: 'ProcessTaskStatusTransitionNode';
  id: string;
  outStatus: {
    __typename: 'ProcessTaskStatusNode';
    id: string;
    displayName: string;
  };
}

export interface TaskSchema {
  __typename: 'ProcessTaskSchemaNode';
  id: string;
  statusLifecycle: {
    __typename: 'ProcessTaskStatusLifecycleNode';
    id: string;
    transitions: Array<Transition>;
  };
}

export type TaskSchemaOrInaccessible = TaskSchema | InaccessibleInfoGraphQL;

export interface StepTask {
  __typename: 'ProcessTaskNode';
  id: string;
  taskSchema: TaskSchemaOrInaccessible;
  status: {
    __typename: 'ProcessTaskStatusNode';
    id: string;
    displayName: string;
  };
}

export interface TaskData {
  status?: WorksheetTaskStatusType;
  lastEditedUser?: HasAvatar;
  timestamp?: string;
  outStatuses?: Record<string, string>; // {[displayName]: id}
  taskIds?: Array<string>;
}

export interface MethodTaskData extends TaskData {
  procedureType?: ProcedureType;
}

export enum DocumentStatus {
  NOT_SELECTED = 'NOT_SELECTED',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
}

export interface LoadedDocument {
  status: DocumentStatus.LOADED;
  id: string;
}

interface LoadingDocument {
  status: DocumentStatus.LOADING;
  id?: never;
}

interface NotSelectedDocument {
  status: DocumentStatus.NOT_SELECTED;
  id?: never;
}

export type WorksheetDocument = LoadedDocument | LoadingDocument | NotSelectedDocument;

export enum WorksheetTaskStatusType {
  SKIPPED = 'SKIPPED',
  SKIPPING = 'SKIPPING',
  COMPLETED = 'COMPLETED',
  COMPLETING = 'COMPLETING',
  FAILED = 'FAILED',
  FAILING = 'FAILING',
  READY = 'READY',
  AMENDING = 'AMENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  STARTING = 'STARTING',
  NOT_READY = 'NOT_READY',
  PENDING = 'PENDING',
}

// The following dictionary maps all the terminal statuses a method task can transition to, to the corresponding
// loading status that should be displayed when all the method tasks for the worksheet are not in that same
// terminal status yet.
export const WorksheetTaskLoadingStateMapping: Record<
  WorksheetTaskStatusType,
  WorksheetTaskStatusType | undefined
> = {
  [WorksheetTaskStatusType.SKIPPED]: WorksheetTaskStatusType.SKIPPING,
  [WorksheetTaskStatusType.COMPLETED]: WorksheetTaskStatusType.COMPLETING,
  [WorksheetTaskStatusType.FAILED]: WorksheetTaskStatusType.FAILING,
  [WorksheetTaskStatusType.IN_PROGRESS]: WorksheetTaskStatusType.STARTING,
  [WorksheetTaskStatusType.READY]: WorksheetTaskStatusType.AMENDING,
  [WorksheetTaskStatusType.SKIPPING]: undefined,
  [WorksheetTaskStatusType.COMPLETING]: undefined,
  [WorksheetTaskStatusType.FAILING]: undefined,
  [WorksheetTaskStatusType.AMENDING]: undefined,
  [WorksheetTaskStatusType.STARTING]: undefined,
  [WorksheetTaskStatusType.NOT_READY]: undefined,
  [WorksheetTaskStatusType.PENDING]: undefined,
};

export const WORKSHEET_STEP_TERMINAL_STATUSES = [
  WorksheetTaskStatusType.COMPLETED,
  WorksheetTaskStatusType.SKIPPED,
  WorksheetTaskStatusType.FAILED,
];

export const WORKSHEET_STEP_LOADING_STATUSES = [
  WorksheetTaskStatusType.AMENDING,
  WorksheetTaskStatusType.COMPLETING,
  WorksheetTaskStatusType.FAILING,
  WorksheetTaskStatusType.SKIPPING,
];

export const DisplayNameToStatusTypeMapping: Record<string, WorksheetTaskStatusType> = {
  [DefaultStatusDisplayName.PENDING]: WorksheetTaskStatusType.PENDING,
  [DefaultStatusDisplayName.READY]: WorksheetTaskStatusType.READY,
  [DefaultStatusDisplayName.COMPLETED]: WorksheetTaskStatusType.COMPLETED,
  [DefaultStatusDisplayName.FAILED]: WorksheetTaskStatusType.FAILED,
  [DefaultStatusDisplayName.SKIPPED]: WorksheetTaskStatusType.SKIPPED,
  [DefaultStatusDisplayName.IN_PROGRESS]: WorksheetTaskStatusType.IN_PROGRESS,
  [DefaultStatusDisplayName.NOT_READY]: WorksheetTaskStatusType.NOT_READY,
};

export const StatusTypeToDisplayNameMapping: Record<WorksheetTaskStatusType, DefaultStatusDisplayName> = {
  [WorksheetTaskStatusType.READY]: DefaultStatusDisplayName.READY,
  [WorksheetTaskStatusType.COMPLETED]: DefaultStatusDisplayName.COMPLETED,
  [WorksheetTaskStatusType.FAILED]: DefaultStatusDisplayName.FAILED,
  [WorksheetTaskStatusType.SKIPPED]: DefaultStatusDisplayName.SKIPPED,
  [WorksheetTaskStatusType.IN_PROGRESS]: DefaultStatusDisplayName.IN_PROGRESS,
  [WorksheetTaskStatusType.NOT_READY]: DefaultStatusDisplayName.NOT_READY,
  [WorksheetTaskStatusType.PENDING]: DefaultStatusDisplayName.PENDING,
  // Following does not map to a task status, but is used to represent a state when all tasks do not have the
  // same status. We map it here to make TS happy.
  [WorksheetTaskStatusType.SKIPPING]: DefaultStatusDisplayName.NOT_READY,
  [WorksheetTaskStatusType.COMPLETING]: DefaultStatusDisplayName.NOT_READY,
  [WorksheetTaskStatusType.FAILING]: DefaultStatusDisplayName.NOT_READY,
  [WorksheetTaskStatusType.STARTING]: DefaultStatusDisplayName.NOT_READY,
  [WorksheetTaskStatusType.AMENDING]: DefaultStatusDisplayName.NOT_READY,
};

export const NO_USER: HasAvatar = {
  handle: 'User Not Found',
  avatar_url: NO_AVATAR_URL,
};

export interface WorksheetStepDocumentSelection {
  stepId?: string;
  stepGroupIndex?: number;
  stepIndex?: number;
  document: WorksheetDocument;
}

export enum WorksheetViewStateType {
  LOADING = 'worksheet.loading',
  INACCESSIBLE = 'worksheet.loaded.inaccessible',
  LOADED = 'worksheet.loaded',
  RELOADING = 'worksheet.reloading',
}

export enum WorksheetViewActionType {
  RELOADING = 'worksheet.reloading',
  LOADED = 'worksheet.loaded',
  PROCEDURE_LOADED = 'worksheet.procedure.loaded',
}

export interface WorksheetViewStateLoading {
  state: WorksheetViewStateType.LOADING;
}

export interface WorksheetViewStateInaccessible {
  state: WorksheetViewStateType.INACCESSIBLE;
  worksheetIsInaccessible: boolean;
  worksheet: InaccessibleInfoGraphQL;
}

export interface WorksheetViewStateAccessible {
  state: WorksheetViewStateType.LOADED | WorksheetViewStateType.RELOADING;
  worksheet: Worksheet;
  // Should the Worksheet allow modifcations of Steps or the underlying Tasks?
  isExecutionEnabled: boolean;
}

export type WorksheetViewState =
  | WorksheetViewStateLoading
  | WorksheetViewStateInaccessible
  | WorksheetViewStateAccessible;

export const INITIAL_WORKSHEET_VIEW_STATE: WorksheetViewState = {
  state: WorksheetViewStateType.LOADING,
};

export interface WorksheetViewLoadedStateAction {
  type: WorksheetViewActionType.LOADED;
  worksheet: WorksheetViewQuery['worksheet'];
  isExecutionEnabled?: boolean;
}

export interface WorksheetViewReloadedStateAction {
  type: WorksheetViewActionType.RELOADING;
  worksheet: WorksheetViewQuery['worksheet'];
}

export interface WorksheetProcedureTypeLoadedStateAction {
  type: WorksheetViewActionType.PROCEDURE_LOADED;
  procedureType: ProcedureType;
}

export type WorksheetViewStateAction =
  | WorksheetViewLoadedStateAction
  | WorksheetViewReloadedStateAction
  | WorksheetProcedureTypeLoadedStateAction;
